import { Clock, Check, Loader2, ShieldCheck, X } from "lucide-react";
import { motion } from "framer-motion";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import MarkdownRenderer from "@/components/ui/markdown-reader";
import { cn } from "@/lib/utils";
import { comunicadoSistemaService } from "@/services/api/comunicado-sistema.service";
import { comunicadoSistemaKeys } from "@/services/api/queries/comunicado-sistema.queries";

import { NIVEL_CONFIG } from "./comunicado-nivel";
import {
  ComunicadoSistemaNivel,
  ComunicadoSistemaVigenteDTO,
} from "@/types/comunicado-sistema.type";

interface Props {
  comunicado: ComunicadoSistemaVigenteDTO | null;
  onOpenChange: (open: boolean) => void;
  /** Prévia do admin: mesmo visual, mas o "Li e aceito" não chama a API. */
  preview?: boolean;
}

/** Gradiente do header por nível (mesma paleta do TarefasVencidasDialog). */
const HEADER_GRADIENT: Record<ComunicadoSistemaNivel, string> = {
  CRITICO: "from-red-500 to-rose-600",
  ALERTA: "from-amber-500 to-orange-600",
  INFORMATIVO: "from-blue-500 to-indigo-600",
};

function formatarJanela(inicioEm: string, fimEm?: string): string {
  const fmt = (d: string) =>
    new Date(d).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  return fimEm ? `${fmt(inicioEm)} até ${fmt(fimEm)}` : `a partir de ${fmt(inicioEm)}`;
}

/** Leitura do conteúdo (markdown) de um comunicado. Aside em dialog, não página. */
export function ComunicadoSistemaDialog({ comunicado, onOpenChange, preview }: Props) {
  const qc = useQueryClient();
  const aceitar = useMutation({
    mutationFn: (id: number) => comunicadoSistemaService.aceitar(id),
    onSuccess: () => {
      // Ao aceitar, o crítico some dos vigentes; recarrega para refletir.
      qc.invalidateQueries({ queryKey: comunicadoSistemaKeys.vigentes() });
      onOpenChange(false);
    },
  });

  if (!comunicado) return null;

  const config = NIVEL_CONFIG[comunicado.nivel];
  const { Icon } = config;

  return (
    <Dialog open={!!comunicado} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92dvh] w-[95vw] max-w-none flex-col gap-0 overflow-hidden rounded-2xl border-0 bg-card p-0 shadow-2xl sm:max-w-[680px] sm:rounded-3xl lg:max-w-[760px] [&>button]:hidden">
        {/* HEADER */}
        <div
          className={cn(
            "relative shrink-0 overflow-hidden bg-gradient-to-br p-5 pb-6 sm:p-8 sm:pb-8",
            HEADER_GRADIENT[comunicado.nivel],
          )}
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
              backgroundSize: "16px 16px",
            }}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -12 }}
            animate={{ opacity: 0.12, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="pointer-events-none absolute -right-6 -top-6 text-white sm:-right-8 sm:-top-8"
          >
            <Icon className="h-40 w-40 sm:h-56 sm:w-56 lg:h-64 lg:w-64" />
          </motion.div>

          {/* Fechar (o "X" padrão está escondido) */}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 z-20 rounded-full bg-white/15 p-1.5 text-white/90 backdrop-blur-md transition-colors hover:bg-white/25 hover:text-white"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>

          <DialogHeader className="relative z-10 text-left">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-semibold text-white shadow-sm backdrop-blur-md sm:mb-3"
            >
              <Icon className="h-3.5 w-3.5" />
              {config.label}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <DialogTitle className="break-words pr-10 text-xl font-bold leading-tight tracking-tight text-white sm:pr-0 sm:text-3xl lg:text-4xl">
                {comunicado.titulo}
              </DialogTitle>
              <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-white/80 sm:mt-2 sm:text-sm">
                <Clock className="h-3.5 w-3.5" />
                {formatarJanela(comunicado.inicioEm, comunicado.fimEm)}
              </p>
            </motion.div>
          </DialogHeader>
        </div>

        {/* BODY */}
        <div className="custom-scrollbar flex-1 overflow-y-auto bg-background p-5 sm:p-8 sm:pt-6">
          {comunicado.conteudo ? (
            <MarkdownRenderer content={comunicado.conteudo} />
          ) : (
            <p className="text-base leading-relaxed text-muted-foreground">{comunicado.resumo}</p>
          )}
        </div>

        {/* FOOTER */}
        <DialogFooter className="flex shrink-0 flex-col gap-2.5 border-t border-border/50 bg-background p-5 pt-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-8 sm:pt-5">
          {comunicado.exigeAceite && !comunicado.jaAceito ? (
            <>
              <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground sm:max-w-xs sm:text-sm">
                <ShieldCheck className={cn("mt-0.5 h-4 w-4 shrink-0", config.accentText)} />
                {preview
                  ? "Prévia: o usuário precisa confirmar a leitura para tirar isto da barra."
                  : "Este comunicado exige que você confirme a leitura para prosseguir."}
              </p>
              <Button
                size="lg"
                onClick={() => (preview ? onOpenChange(false) : aceitar.mutate(comunicado.id))}
                disabled={aceitar.isPending}
                className="h-12 w-full shrink-0 bg-red-600 text-base text-white shadow-lg transition-all duration-300 hover:bg-red-700 sm:w-auto sm:px-8"
              >
                {aceitar.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Li e aceito
              </Button>
            </>
          ) : (
            <>
              {comunicado.jaAceito && (
                <p className="flex items-center gap-2 text-xs font-medium text-emerald-600 dark:text-emerald-400 sm:text-sm">
                  <ShieldCheck className="h-4 w-4 shrink-0" />
                  Você já confirmou a leitura deste comunicado.
                </p>
              )}
              <Button
                size="lg"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="h-12 w-full shrink-0 text-base transition-all duration-300 sm:ml-auto sm:w-auto sm:px-8"
              >
                Fechar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
